import { input, ChangeDetectionStrategy, Component, computed, OnDestroy } from '@angular/core';
import { DeclarativeMapBase } from '../declarative-map-base/declarative-map-base.component';
import { computedPrevious } from 'ngxtension/computed-previous';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { outputFromObservable } from '@angular/core/rxjs-interop';
import { filter, map, takeUntil } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { MapPane } from '@cue/map/core';

@Component({
  selector: 'cue-declarative-map-polyline',
  imports: [],
  templateUrl: './declarative-map-polyline.component.html',
  styleUrl: './declarative-map-polyline.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeclarativeMapPolylineComponent extends DeclarativeMapBase implements OnDestroy {
  polyline = input<{ lat: number; lng: number }[]>();
  zIndexOffset = input<number>(0);
  className = input<string>();
  color = input<string>('#820B8A');
  pane = input<MapPane>();
  polylineProperties = computed(() => ({
    color: this.color(),
    pane: this.pane(),
    pointlist: this.polyline(),
    className: this.className(),
    zIndexOffset: this.zIndexOffset(),
  }));

  #polylineAdded = false;

  terminator = injectDestroy();

  repainted = outputFromObservable(
    this.adapter.polylineRepainted.pipe(
      takeUntil(this.terminator),
      filter((x) => x === this.identifier),
      map(() => this)
    )
  );

  appended = outputFromObservable(
    this.adapter.polylineAppended.pipe(
      takeUntil(this.terminator),
      filter((x) => x === this.identifier),
      map(() => this)
    )
  );

  previousPolylineProperties = computedPrevious(this.polylineProperties);

  affectPolyline = explicitEffect([this.polylineProperties, this.previousPolylineProperties], () => {
    if (!this.#polylineAdded) {
      this.addPolyline();
      this.#polylineAdded = true;
    } else {
      this.editPolyline();
    }
    return null;
  });

  builPatch() {
    const patch: Partial<typeof current> = {};
    const previous = this.previousPolylineProperties();
    const current = this.polylineProperties();
    if (previous) {
      if (previous.color !== current.color) {
        patch.color = current.color;
      }

      if (previous.pane !== current.pane) {
        patch.pane = current.pane;
      }
      if (previous.className !== current.className || current.className !== undefined) {
        patch.className = current.className;
      }

      if (previous.pointlist !== current.pointlist) {
        patch.pointlist = current.pointlist;
      }
      if (previous.zIndexOffset !== current.zIndexOffset) {
        patch.zIndexOffset = current.zIndexOffset;
      }
    }
    return patch;
  }

  focus() {
    this.adapter.focusPolyline(this.identifier);
  }

  addPolyline() {
    this.adapter.addPolyline(this.identifier, this.polylineProperties());
  }

  editPolyline() {
    const patch = this.builPatch();
    this.adapter.editPolyline(this.identifier, patch);
  }

  ngOnDestroy() {
    this.adapter.removePolyline(this.identifier);
  }
}
